import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Pause, Play, Trash2, ExternalLink } from 'lucide-react';

const STATUS_LABEL = { published: 'Pubblicata', suspended: 'Sospesa', sold: 'Venduta' };
const STATUS_TONE = {
  published: 'bg-accent text-accent-foreground',
  suspended: 'bg-terracotta/20 text-terracotta',
  sold: 'bg-secondary',
};

export default function AdminBatches() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') || '';
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [action, setAction] = useState(null); // {kind, batch}
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/batches', { params: { status: status || undefined, q: q || undefined } });
    setItems(data || []);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!action) return;
    const { kind, batch } = action;
    try {
      if (kind === 'delete') {
        await api.delete(`/admin/batches/${batch.batch_id}`);
        toast.success('Partita eliminata');
      } else {
        await api.post(`/admin/batches/${batch.batch_id}/${kind}`, { reason: reason || null });
        toast.success(`Partita ${kind === 'suspend' ? 'sospesa' : 'riattivata'}`);
      }
      setAction(null); setReason('');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = (s) => setSp(s ? { status: s } : {});

  return (
    <div className="space-y-6" data-testid="admin-batches">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Marketplace</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Partite di prodotti</h1>
        <p className="mt-2 text-sm text-muted-foreground">Gestisci le partite pubblicate dagli agricoltori. Ogni azione è registrata nell'Audit Log.</p>
      </header>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap gap-3">
        <Input placeholder="Cerca titolo, proprietario o frutto" value={q} onChange={e=>setQ(e.target.value)} className="max-w-sm" data-testid="admin-batches-search"/>
        <Select value={status || 'all'} onValueChange={v=>setStatus(v==='all'?'':v)}>
          <SelectTrigger className="w-[200px]" data-testid="admin-batches-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="published">Pubblicate</SelectItem>
            <SelectItem value="suspended">Sospese</SelectItem>
            <SelectItem value="sold">Vendute</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {items.length===0 ? <div className="p-10 text-sm text-muted-foreground text-center">Nessuna partita da mostrare</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3">Titolo</th>
                  <th className="px-4 py-3">Frutto</th>
                  <th className="px-4 py-3">Proprietario</th>
                  <th className="px-4 py-3">Quantità</th>
                  <th className="px-4 py-3">Prezzo</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(b => (
                  <tr key={b.batch_id} data-testid={`admin-batch-row-${b.batch_id}`}>
                    <td className="px-4 py-3">
                      <Link className="font-medium hover:underline flex items-center gap-1" to={`/partite/${b.batch_id}`} target="_blank">
                        {b.title || '—'} <ExternalLink size={12}/>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">{b.fruit_type || '—'}</td>
                    <td className="px-4 py-3">{b.owner_name}</td>
                    <td className="px-4 py-3">{b.quantity_quintals ? `${b.quantity_quintals} q` : '—'}</td>
                    <td className="px-4 py-3">
                      {b.price_per_quintal ? `€ ${new Intl.NumberFormat('it-IT').format(b.price_per_quintal)}/q` :
                       b.total_price ? `€ ${new Intl.NumberFormat('it-IT').format(b.total_price)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[b.status]||'bg-secondary'}`}>{STATUS_LABEL[b.status]||b.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{b.created_at ? new Date(b.created_at).toLocaleDateString('it-IT') : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {b.status === 'published' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'suspend', batch:b})} data-testid={`admin-batch-suspend-${b.batch_id}`}><Pause size={12} className="mr-1"/> Sospendi</Button>}
                        {b.status === 'suspended' && <Button size="sm" variant="outline" onClick={()=>setAction({kind:'reactivate', batch:b})} data-testid={`admin-batch-reactivate-${b.batch_id}`}><Play size={12} className="mr-1"/> Riattiva</Button>}
                        <Button size="sm" variant="outline" onClick={()=>setAction({kind:'delete', batch:b})} data-testid={`admin-batch-delete-${b.batch_id}`}><Trash2 size={12} className="mr-1"/> Elimina</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      <Dialog open={!!action} onOpenChange={o=>{ if(!o){setAction(null); setReason('');} }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Conferma azione ({action?.kind})</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Partita: <b>{action?.batch?.title || action?.batch?.batch_id}</b></div>
          {action?.kind !== 'delete' && (
            <Textarea rows={3} placeholder="Motivo (opzionale ma consigliato per audit log)" value={reason} onChange={e=>setReason(e.target.value)}/>
          )}
          {action?.kind === 'delete' && (
            <div className="text-xs text-destructive">Attenzione: l'eliminazione è definitiva e non può essere annullata.</div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={()=>{setAction(null); setReason('');}}>Annulla</Button>
            <Button onClick={doAction} data-testid="admin-batch-confirm-action">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
